import { useMutation, useQueryClient } from '@tanstack/react-query';

import { passwordLogin } from '../../api/password.api';
import type { PasswordLoginInput } from '../../api/password.api';
import { codeEffectOf } from '../../domain/codeEffect';
import { isIdentifierRefusal, isTransportFailure, toRefusal } from '../../domain/refusal';
import { startSession } from '../../services/startSession';
import type { IdentifyResult, OpenedSession } from '../../types/domain.types';
import { identifyQueryOptions } from '../queries/identifyQuery';

import { errorOf } from './loginFailures';
import { passcodeMutationKeys } from './mutationKeys';
import { clearSettled } from './useLoginMachine';
import type { LoginMachine } from './useLoginMachine';

interface LookupInput {
  readonly identifier: string;
  readonly epoch: number;
}

interface PasswordLoginVariables extends PasswordLoginInput {
  readonly epoch: number;
}

export interface PasswordLoginLogic {
  readonly lookingUp: boolean;
  readonly loggingIn: boolean;
  readonly paused: boolean;
  readonly lookupError: Error | null;
  readonly loginError: Error | null;
  readonly lookedUp: IdentifyResult | undefined;
  readonly choosePassword: (identifier: string | null) => void;
  readonly logIn: (identifier: string | null, password: string) => void;
  readonly clearLookup: () => void;
  readonly clearLogin: () => void;
}

export interface PasswordLoginOptions {
  readonly submittable: string | null;
  readonly onOpened: (session: OpenedSession) => void;
}

export const usePasswordLogin = (machine: LoginMachine, { submittable, onOpened }: PasswordLoginOptions): PasswordLoginLogic => {
  const queryClient = useQueryClient();
  const { dispatch, latest, inFlight } = machine;
  const lookup = useMutation({
    mutationKey: passcodeMutationKeys.lookup(),
    mutationFn: ({ identifier }: LookupInput) => queryClient.fetchQuery(identifyQueryOptions(identifier)),
  });
  const loginer = useMutation({
    mutationKey: passcodeMutationKeys.passwordLogin(),
    mutationFn: ({ identifier, password }: PasswordLoginVariables) => passwordLogin({ identifier, password }, undefined),
    onSuccess: (session) => startSession(queryClient, session, 'password'),
  });

  const choosePassword = (identifier: string | null): void => {
    if (inFlight(passcodeMutationKeys.all())) return;
    if (identifier === null) {
      dispatch({ type: 'problemFound' });
      return;
    }
    const { epoch } = latest();
    lookup.mutate(
      { identifier, epoch },
      {
        onSuccess: (found) =>
          dispatch(found.passwordSet ? { type: 'passwordChosen', epoch } : { type: 'noPassword', epoch, reason: 'NO_PASSWORD_SET' }),
        onError: (failure) => {
          if (!isTransportFailure(failure) && isIdentifierRefusal(toRefusal(failure))) dispatch({ type: 'problemFound' });
          else dispatch({ type: 'passwordChosen', epoch });
        },
      },
    );
  };

  const logIn = (identifier: string | null, password: string): void => {
    if (inFlight(passcodeMutationKeys.all())) return;
    if (identifier === null) {
      dispatch({ type: 'problemFound' });
      return;
    }
    if (password === '') {
      dispatch({ type: 'passwordMissing' });
      return;
    }
    const { epoch } = latest();
    loginer.mutate(
      { identifier, password, epoch },
      {
        onSuccess: onOpened,
        onError: (failure) => {
          if (isTransportFailure(failure)) return;
          const refusal = toRefusal(failure);
          const { hold } = codeEffectOf(failure);
          if (hold?.kind === 'locked') {
            dispatch({ type: 'locked', epoch, ask: { identifier, purpose: 'LOGIN' }, seconds: hold.seconds, at: Date.now() });
          } else if (refusal.code === 'NO_PASSWORD_SET' || refusal.code === 'USE_CODE') {
            dispatch({ type: 'noPassword', epoch, reason: refusal.code });
          } else if (isIdentifierRefusal(refusal)) {
            dispatch({ type: 'problemFound' });
          }
        },
      },
    );
  };

  return {
    lookingUp: lookup.isPending,
    loggingIn: loginer.isPending,
    paused: lookup.isPaused || loginer.isPaused,
    lookupError: errorOf(lookup, machine.state.epoch),
    loginError: errorOf(loginer, machine.state.epoch),
    lookedUp: lookup.data !== undefined && lookup.variables?.identifier === submittable ? lookup.data : undefined,
    choosePassword,
    logIn,
    clearLookup: () => clearSettled(machine, lookup, passcodeMutationKeys.lookup()),
    clearLogin: () => clearSettled(machine, loginer, passcodeMutationKeys.passwordLogin()),
  };
};
